import * as yup from "yup";

import {
    CreateEmissionsSourceFormValues,
    buildStep1ValidationFields,
} from "./FormValues";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormTextInput from "../FormTextInput";
import Typography from "../Typography";
import Button from "../Button";
import { useUniqueReferenceIsUnique } from "../../api/emissions-sources";
import { Location } from "../../api/models/Location";
import FormDropdown from "../FormDropdown";

export type Step1BasicInfoProps = {
    onSubmit: (values: Step1BasicInfoValues, finished: boolean) => void;
    onCancel: () => void;
    defaultValues: Step1BasicInfoValues;
    locations: Location[];
    isLoadingLocations: boolean;
};

type Step1BasicInfoValues = Pick<
    CreateEmissionsSourceFormValues,
    "name" | "uniqueReference" | "locationId"
>;

const Step1BasicInfo = ({
    onSubmit,
    onCancel,
    defaultValues,
    locations,
    isLoadingLocations,
}: Step1BasicInfoProps) => {
    const uniqueReferenceIsUnique = useUniqueReferenceIsUnique();

    const validationSchema: yup.SchemaOf<Step1BasicInfoValues> = yup.object(
        buildStep1ValidationFields(uniqueReferenceIsUnique)
    );

    const methods = useForm<Step1BasicInfoValues>({
        mode: "onSubmit",
        resolver: yupResolver(validationSchema),
        defaultValues,
    });

    const locationOptions = locations
        ? locations.map((location) => ({
              value: location.id,
              label: location.name,
          }))
        : [];

    const handleSubmit = (values: Step1BasicInfoValues) => {
        onSubmit(values, false);
    };

    return (
        <form
            onSubmit={methods.handleSubmit(handleSubmit)}
            className="rounded-xl bg-surface-light-1 p-6 space-y-4"
            noValidate
        >
            <Typography variant="title-large" component="h2" className="mb-6">
                Basic information
            </Typography>
            <FormTextInput
                required
                label="Name"
                placeholder="Enter emissions source name"
                {...methods.register("name")}
                error={methods.formState.errors.name}
            />
            <FormTextInput
                required
                label="Unique reference"
                placeholder="Enter unique reference"
                {...methods.register("uniqueReference")}
                error={methods.formState.errors.uniqueReference}
            />
            <Controller
                name="locationId"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                    <FormDropdown
                        required
                        isLoadingOptions={isLoadingLocations}
                        label="Location"
                        pleaseSelectText="Select location"
                        options={locationOptions}
                        value={value}
                        onChange={onChange}
                        error={methods.formState.errors.locationId}
                    />
                )}
            />
            <div className="flex justify-end pt-4">
                <Button variant="transparent" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit">Next</Button>
            </div>
        </form>
    );
};

export default Step1BasicInfo;
